import {
  FormControl,
  FormHelperText,
  TextField,
  Typography
} from '@mui/material'
import React, { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'

import { URLHelperText } from 'components/services/create/URLHelperText'
import {
  getExistingStatusCheckByMonitorUrl,
  StatusCheckFormValues,
  StatusCheckMonitorSelectionRadioValue
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import { useTranslation } from 'hooks/use-translation'

export const DatadogMonitorURL: React.FC<{
  radioValue: StatusCheckMonitorSelectionRadioValue
}> = ({ radioValue }) => {
  const { trans } = useTranslation()
  const { control, formState } = useFormContext<StatusCheckFormValues>()

  const [externalIntegration, observabilityTool, monitorUrl] =
    useWatch<StatusCheckFormValues>({
      control,
      name: ['externalIntegration', 'observabilityTool', 'monitorUrl']
    })

  const { statusChecks: existingStatusChecks } = useStatusChecksContext()
  const statusChecksUrls = getStatusChecksUrls()

  const existingStatusCheck = useMemo(() => {
    return getExistingStatusCheckByMonitorUrl(
      existingStatusChecks,
      observabilityTool,
      monitorUrl
    )
  }, [existingStatusChecks, monitorUrl, observabilityTool])

  return (
    <FormControl>
      <Controller
        control={control}
        name="monitorUrl"
        render={({ field }) => (
          <TextField
            {...field}
            aria-describedby="monitor-url-helper-text"
            data-testid="status-check-monitor-url-input"
            disabled={radioValue !== 'url'}
            error={Boolean(formState.errors.monitorUrl)}
            helperText={formState.errors.monitorUrl?.message}
            label={trans(
              'forms.save_status_check.connect_monitor.monitor_url.text_field.label'
            )}
            variant="outlined"
          />
        )}
      />
      {formState.errors.monitorUrl?.type ===
        'is-datadog-monitor-url-already-used' && (
        <FormHelperText id="link-to-existing-health-check">
          <Typography variant="caption">
            <RouterLink to={statusChecksUrls.edit(existingStatusCheck?.id)}>
              {trans(
                'forms.save_status_checks.monitor_url.existing_status_check.link.text'
              )}
            </RouterLink>
          </Typography>
        </FormHelperText>
      )}
      <FormHelperText id="monitor-url-helper-text">
        <URLHelperText integration={externalIntegration} />
      </FormHelperText>
    </FormControl>
  )
}
